import type {
  AvailabilitySlot,
  ClinicalInsuranceCategory,
  Consultation,
  ConsultationConsultType,
  ConsultationId,
  CustomerGender,
  CustomerId,
} from '@gh/common/models'
import { CLINICAL_BOOKING_INVALIDATED_CODE } from '@gh/common/models'
import { FirebaseError } from 'firebase/app'
import { firestoreTimestampToDate } from '@/components/utils'
import { getTimezoneForStateName } from '@gh/common/utils'
import {
  formatClinicalBookingDate,
  formatClinicalSlotRange,
} from '@/components/consultation/clinicalBookingFormat'

/** Happy-path booking UI steps — ids match `lifecycleStatus`. */
export type ClinicalBookingStep =
  | 'draft'
  | 'reserved'
  | 'ensuring_bridge_service'
  | 'payment_pending'
  | 'provisioning'
  | 'scheduled'
  | 'pending_settlement_payment'
  | 'reservation_expired'

export type ClinicalDraftSubPhase = 'slot-picker'

export type ClinicalReservedSubPhase = 'insured_coverage' | null

export type ClinicalBillingPath = 'insured' | 'self_pay'

export type ClinicalCustomerData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  state: string
  fullName: string
  id: string
  dateOfBirth?: string
  gender?: CustomerGender
}

export type ClinicalProfileDraft = {
  firstName: string
  lastName: string
  state: string
  stateName: string
  dateOfBirth: string
  gender: CustomerGender
}

export function consultationSnapshotFieldsToDates(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const dateFields = [
    'createdAt',
    'updatedAt',
    'scheduledStart',
    'scheduledEnd',
    'checkoutExpiresAt',
    'reservedAt',
  ] as const
  const normalized = { ...data }
  for (const field of dateFields) {
    if (field in normalized && normalized[field] != null) {
      normalized[field] = firestoreTimestampToDate(normalized[field])
    }
  }
  return normalized
}

export const CLINICAL_BOOKING_STEPS_IN_ORDER: ClinicalBookingStep[] = [
  'draft',
  'reserved',
  'ensuring_bridge_service',
  'payment_pending',
  'provisioning',
  'scheduled',
  'pending_settlement_payment',
]

export const CLINICAL_BOOKING_STEP_LABELS: Record<ClinicalBookingStep, string> = {
  draft: 'Draft',
  reserved: 'Reserved',
  ensuring_bridge_service: 'Ensuring Bridge service',
  payment_pending: 'Payment pending',
  provisioning: 'Provisioning',
  scheduled: 'Scheduled',
  pending_settlement_payment: 'Settlement payment',
  reservation_expired: 'Reservation expired',
}

/**
 * STE100 product steps — customer-path UX SSOT.
 * Maps to lifecycle steps via `resolveClinicalProductStep` / store orchestration.
 */
export type ClinicalProductStep =
  | 'intro'
  | 'existing_consultation_gate'
  | 'appointment_cancelled'
  | 'unsupported_state'
  | 'profile'
  | 'insurance_category'
  | 'cash_pay_eligible'
  | 'insurance_on_file'
  | 'insurance_details'
  | 'insurance_verified'
  | 'slot_selection'
  | 'hard_verification'
  | 'payment'
  | 'confirmed'

export type ClinicalCashPayEligibleReason = 'no_insurance' | 'unsupported_category'

export type ClinicalInsuranceDetailsEntry = {
  errorMessage: string
  payerId: string | null
  payerDisplayName: string | null
  memberId: string | null
}

/** Maps insurance category to cash-pay eligible screen copy variant. */
export function resolveCashPayEligibleReason(
  category: ClinicalInsuranceCategory | null | undefined,
): ClinicalCashPayEligibleReason {
  if (category === 'no_insurance') {
    return 'no_insurance'
  }
  return 'unsupported_category'
}

export {
  clinicalBookingSupportedStateCodes,
  isClinicalBookingStateSupported,
} from '@gh/common/models'

export type { ClinicalInsuranceCategory } from '@gh/common/models'
export {
  CLINICAL_INSURANCE_CATEGORY_OPTIONS,
  insuranceCategoryBillingPath,
} from '@gh/common/models'

export type ClinicalInsuredPaymentFailureReason =
  'policy_invalid' | 'uninsured' | 'ineligible' | 'ineffective' | 'unknown'

export const CLINICAL_PRODUCT_STEP_LABELS: Record<ClinicalProductStep, string> = {
  intro: 'Introduction',
  existing_consultation_gate: 'Existing appointment',
  appointment_cancelled: 'Appointment cancelled',
  unsupported_state: 'Unsupported state',
  profile: 'Confirm profile',
  insurance_category: 'Insurance category',
  cash_pay_eligible: 'Cash pay eligible',
  insurance_on_file: 'Insurance on file',
  insurance_details: 'Insurance details',
  insurance_verified: 'Insurance verified',
  slot_selection: 'Slot selection',
  hard_verification: 'Insurance verification',
  payment: 'Payment',
  confirmed: 'Confirmed',
}

export function isProductPreflowStep(step: ClinicalProductStep): boolean {
  return (
    step === 'intro' ||
    step === 'existing_consultation_gate' ||
    step === 'appointment_cancelled' ||
    step === 'profile' ||
    step === 'insurance_category' ||
    step === 'cash_pay_eligible' ||
    step === 'insurance_on_file' ||
    step === 'insurance_details' ||
    step === 'insurance_verified'
  )
}

/** Scheduled/provisioning consultation from another booking session blocks a new booking. */
export function isConflictingScheduledConsultation(args: {
  scheduledConsultationId: string | null | undefined
  sessionConsultationId: string | null | undefined
}): boolean {
  if (!args.scheduledConsultationId) {
    return false
  }
  if (
    args.sessionConsultationId != null &&
    args.scheduledConsultationId === args.sessionConsultationId
  ) {
    return false
  }
  return true
}

const GATE_OCCUPYING_PRIORITY: Partial<Record<Consultation['lifecycleStatus'], number>> = {
  scheduled: 2,
  provisioning: 2,
}

/**
 * Pick which consultation occupies the existing-consultation gate when several match.
 * `pending_cancellation` is shown as cancelled and does not occupy the gate.
 */
export function pickGateOccupyingConsultation(consultations: Consultation[]): Consultation | null {
  const occupying = consultations.filter(
    (consultation) => consultation.lifecycleStatus !== 'pending_cancellation',
  )
  if (occupying.length === 0) {
    return null
  }

  const sorted = [...occupying].sort((a, b) => {
    const priorityA = GATE_OCCUPYING_PRIORITY[a.lifecycleStatus] ?? 0
    const priorityB = GATE_OCCUPYING_PRIORITY[b.lifecycleStatus] ?? 0
    const priorityDelta = priorityB - priorityA
    if (priorityDelta !== 0) {
      return priorityDelta
    }
    return b.createdAt.getTime() - a.createdAt.getTime()
  })

  return sorted[0] ?? null
}

/** True while the customer is still in the booking session immediately after checkout. */
export function isInPostPaymentConfirmationSession(args: {
  customerCheckoutAcknowledged: boolean
  customerAwaitingCheckoutPropagation: boolean
  consultationId?: string | null
}): boolean {
  if (args.customerCheckoutAcknowledged || args.customerAwaitingCheckoutPropagation) {
    return true
  }
  if (args.consultationId != null && isClinicalCheckoutRecentlySubmitted(args.consultationId)) {
    return true
  }
  return false
}

export function resolveClinicalProductStep(args: {
  productStep: ClinicalProductStep | null
  productPreflowComplete: boolean
  hasScheduledConsultation: boolean
  currentStep: ClinicalBookingStep
  draftSubPhase: ClinicalDraftSubPhase
  reservedSubPhase: ClinicalReservedSubPhase
  billingPath: ClinicalBillingPath | null
  isResume: boolean
  inPostPaymentConfirmationSession: boolean
}): ClinicalProductStep | null {
  if (!args.productPreflowComplete && args.productStep) {
    if (args.productStep === 'intro' && args.hasScheduledConsultation) {
      return 'existing_consultation_gate'
    }
    return args.productStep
  }

  if (args.currentStep === 'draft' && args.draftSubPhase === 'slot-picker') {
    return 'slot_selection'
  }
  if (
    args.currentStep === 'reserved' &&
    args.billingPath === 'insured' &&
    args.reservedSubPhase === 'insured_coverage'
  ) {
    return 'hard_verification'
  }
  if (args.currentStep === 'ensuring_bridge_service' && args.billingPath === 'insured') {
    return 'hard_verification'
  }
  if (args.currentStep === 'ensuring_bridge_service') {
    return 'payment'
  }
  if (args.currentStep === 'payment_pending') {
    return 'payment'
  }
  if (args.currentStep === 'pending_settlement_payment') {
    return 'payment'
  }
  if (args.currentStep === 'scheduled') {
    if (args.inPostPaymentConfirmationSession) {
      return 'confirmed'
    }
    if (args.hasScheduledConsultation) {
      return 'existing_consultation_gate'
    }
    return 'confirmed'
  }

  if (!args.isResume && !args.productPreflowComplete) {
    return args.hasScheduledConsultation ? 'existing_consultation_gate' : 'intro'
  }

  return null
}

export const CLINICAL_CONSULT_TYPE_LABELS: Record<ConsultationConsultType, string> = {
  clinical_main: 'Initial consultation, 60 minutes',
  clinical_followup: 'Follow-up consultation, 30 minutes',
}

/** Mirrors `CLINICAL_CONSULT_DURATION_MINUTES` in common clinicalConsultConfig (frontend-safe). */
export const CLINICAL_CONSULT_DURATION_MINUTES: Record<ConsultationConsultType, number> = {
  clinical_main: 60,
  clinical_followup: 30,
}

export const CLINICAL_CONSULT_TYPES: ConsultationConsultType[] = [
  'clinical_main',
  'clinical_followup',
]

export function shouldShowClinicalBookingCancel(params: {
  showCancel: boolean
  currentStep: ClinicalBookingStep
  paymentComplete: boolean
  customerCheckoutSubmitted: boolean
}): boolean {
  if (!params.showCancel) return false
  if (params.currentStep === 'reservation_expired') {
    return false
  }
  if (params.currentStep === 'draft' || params.currentStep === 'reserved') {
    return true
  }
  if (
    params.currentStep === 'payment_pending' &&
    !params.paymentComplete &&
    !params.customerCheckoutSubmitted
  ) {
    return true
  }
  return false
}

/**
 * Derive booking UI step from loaded consultation lifecycle.
 * Hold-expiry terminal statuses map to `reservation_expired` (server cancels via snapshot).
 *
 * Decision: `draft` must NOT be the default for unknown or post-scheduled lifecycle
 * statuses — that would reopen an in-flight booking as a fresh draft. Proper
 * unsupported-lifecycle UX (oops page / exit modal / error toast) is deferred;
 * until then, fail closed to `scheduled` for post-scheduled and unrecognized values.
 */
export function resolveClinicalBookingStep(
  consultation: Consultation | null | undefined,
): ClinicalBookingStep {
  if (!consultation) {
    return 'draft'
  }
  switch (consultation.lifecycleStatus) {
    case 'draft':
      return 'draft'
    case 'reserved':
    case 'pending_noshow_fee':
      return 'reserved'
    case 'pending_cancellation':
    case 'cancelled':
    case 'cancel_failed':
      return 'reservation_expired'
    case 'ensuring_bridge_service':
      return 'ensuring_bridge_service'
    case 'payment_pending':
      return 'payment_pending'
    case 'provisioning':
      return 'provisioning'
    case 'scheduled':
      return 'scheduled'
    // TODO: dedicated post-scheduled / unsupported-lifecycle UX — temporary fail-closed mapping.
    case 'settlement_triage':
    case 'ensuring_bridge_billing':
    case 'bridge_settling':
    case 'bridge_triage':
    case 'completed':
      return 'scheduled'
    case 'pending_settlement_payment':
      return 'pending_settlement_payment'
    default:
      // TODO: unsupported lifecycle — show oops/exit UX instead of reopening booking.
      return 'scheduled'
  }
}

/**
 * Customer-facing booking step. Maps backend `provisioning` to `scheduled` so
 * customers see confirmation immediately after payment; admin keeps lifecycle truth.
 */
export function resolveClinicalBookingDisplayStep(
  consultation: Consultation | null | undefined,
): ClinicalBookingStep {
  const lifecycleStep = resolveClinicalBookingStep(consultation)
  if (lifecycleStep === 'provisioning') {
    return 'scheduled'
  }
  return lifecycleStep
}

export function resolveReservedSubPhaseForResume(args: {
  billingPath: ClinicalBillingPath | null | undefined
  lifecycleStatus: Consultation['lifecycleStatus'] | undefined
  insuredCoverageAcknowledged: boolean
}): ClinicalReservedSubPhase {
  if (
    args.billingPath === 'insured' &&
    args.lifecycleStatus === 'reserved' &&
    !args.insuredCoverageAcknowledged
  ) {
    return 'insured_coverage'
  }
  return null
}

export function shouldAutoConfirmReservedSelfPayOnResume(args: {
  billingPath: ClinicalBillingPath | null | undefined
  lifecycleStatus: Consultation['lifecycleStatus'] | undefined
}): boolean {
  return args.lifecycleStatus === 'reserved' && args.billingPath === 'self_pay'
}

export function resolveDraftSubPhase(_args: {
  consultTypeLocked: boolean
  consultTypeChosen: ConsultationConsultType | null
}): ClinicalDraftSubPhase {
  return 'slot-picker'
}

const LIFECYCLE_RANK: Record<Consultation['lifecycleStatus'], number> = {
  draft: 0,
  pending_cancellation: 0,
  pending_noshow_fee: 0,
  cancelled: 0,
  cancel_failed: 0,
  reserved: 1,
  ensuring_bridge_service: 2,
  payment_pending: 3,
  provisioning: 4,
  scheduled: 5,
  ensuring_bridge_billing: 6,
  bridge_settling: 7,
  bridge_triage: 8,
  settlement_triage: 9,
  pending_settlement_payment: 10,
  completed: 11,
}

/** Merge Firestore consultation with an optimistic UI patch. */
export function mergeConsultation(
  base: Consultation | null,
  patch: Partial<Consultation> | null,
): Consultation | null {
  if (!patch) {
    return base
  }
  if (!base) {
    return hasRequiredConsultationFields(patch) ? (patch as Consultation) : null
  }
  return { ...base, ...patch }
}

function hasRequiredConsultationFields(value: Partial<Consultation>): value is Consultation {
  return (
    typeof value.id === 'string' &&
    typeof value.customerId === 'string' &&
    value.featureKey === 'clinical_consultation' &&
    typeof value.consultType === 'string' &&
    typeof value.lifecycleStatus === 'string' &&
    typeof value.operationalStatus === 'string' &&
    value.createdAt instanceof Date &&
    value.updatedAt instanceof Date
  )
}

/** True when the server snapshot has caught up to or passed the optimistic patch. */
export function shouldClearOptimisticPatch(
  server: Consultation,
  patch: Partial<Consultation> | null,
): boolean {
  if (!patch?.lifecycleStatus) {
    return false
  }

  const patchStatus = patch.lifecycleStatus
  const serverStatus = server.lifecycleStatus

  if (patchStatus === 'draft') {
    return serverStatus === 'draft' || LIFECYCLE_RANK[serverStatus] >= LIFECYCLE_RANK.reserved
  }
  if (patchStatus === 'reserved') {
    if (
      serverStatus === 'draft' ||
      serverStatus === 'cancelled' ||
      serverStatus === 'cancel_failed' ||
      serverStatus === 'pending_cancellation'
    ) {
      return true
    }
    return LIFECYCLE_RANK[serverStatus] >= LIFECYCLE_RANK.reserved
  }
  if (patchStatus === 'ensuring_bridge_service') {
    return LIFECYCLE_RANK[serverStatus] >= LIFECYCLE_RANK.ensuring_bridge_service
  }
  if (patchStatus === 'payment_pending') {
    return LIFECYCLE_RANK[serverStatus] >= LIFECYCLE_RANK.payment_pending
  }
  if (patchStatus === 'pending_cancellation') {
    return (
      serverStatus === 'pending_cancellation' ||
      serverStatus === 'pending_noshow_fee' ||
      serverStatus === 'cancelled' ||
      serverStatus === 'cancel_failed' ||
      serverStatus === 'draft'
    )
  }
  if (patchStatus === 'pending_noshow_fee') {
    return (
      serverStatus === 'pending_noshow_fee' ||
      serverStatus === 'cancelled' ||
      serverStatus === 'draft'
    )
  }
  return false
}

/** True when merged UI lifecycle may not match authoritative server state yet. */
export function isConsultationLifecycleStale(args: {
  consultationId: string | null
  hasActiveSubscription: boolean
  snapshotLoading: boolean
  hasServerSnapshot: boolean
  displayedLifecycleStatus: Consultation['lifecycleStatus'] | undefined
  authoritativeLifecycleStatus: Consultation['lifecycleStatus'] | null
}): boolean {
  if (!args.consultationId || !args.hasActiveSubscription) {
    return false
  }
  if (args.snapshotLoading) {
    return true
  }
  if (args.hasServerSnapshot && args.authoritativeLifecycleStatus === null) {
    return true
  }
  if (args.authoritativeLifecycleStatus === null) {
    return false
  }
  return args.displayedLifecycleStatus !== args.authoritativeLifecycleStatus
}

export type ClinicalSlotSelection = {
  date: Date
  slot: AvailabilitySlot
}

/** Derive calendar date + availability slot from consultation schedule fields. */
export function slotSelectionFromConsultation(
  consultation: Consultation | null | undefined,
): ClinicalSlotSelection | null {
  if (!consultation?.startTimeInMS || !consultation.durationMinutes) {
    return null
  }
  const startTime = new Date(consultation.startTimeInMS)
  const endTime = new Date(consultation.startTimeInMS + consultation.durationMinutes * 60_000)
  return {
    date: new Date(startTime.getFullYear(), startTime.getMonth(), startTime.getDate()),
    slot: {
      id: `${consultation.tellescopeUserId ?? 'unknown'}:${consultation.startTimeInMS}:${consultation.durationMinutes}`,
      startTime,
      endTime,
      duration: consultation.durationMinutes,
      available: true,
      userIds: consultation.tellescopeUserId ? [consultation.tellescopeUserId] : [],
    },
  }
}

export type ClinicalAccountConsultationCardMode = 'fresh' | 'in_progress' | 'scheduled'

const PRE_SCHEDULED_CLINICAL_LIFECYCLE_STATUSES = new Set<Consultation['lifecycleStatus']>([
  'draft',
  'reserved',
  'ensuring_bridge_service',
  'payment_pending',
])

const TERMINAL_CLINICAL_OPERATIONAL_STATUSES = new Set<Consultation['operationalStatus']>([
  'completed',
  'no_show',
  'cancelled',
])

const SCHEDULED_CLINICAL_LIFECYCLE_STATUSES = new Set<Consultation['lifecycleStatus']>([
  'provisioning',
  'scheduled',
])

/** Account card mode from the customer's active clinical consultation snapshot. */
export function resolveClinicalAccountConsultationCardMode(
  consultation: Consultation | null | undefined,
): ClinicalAccountConsultationCardMode {
  if (!consultation) {
    return 'fresh'
  }

  if (consultation.bookingHiddenOnCustomerUi === true) {
    return 'fresh'
  }

  if (TERMINAL_CLINICAL_OPERATIONAL_STATUSES.has(consultation.operationalStatus)) {
    return 'fresh'
  }

  if (SCHEDULED_CLINICAL_LIFECYCLE_STATUSES.has(consultation.lifecycleStatus)) {
    return 'scheduled'
  }

  if (PRE_SCHEDULED_CLINICAL_LIFECYCLE_STATUSES.has(consultation.lifecycleStatus)) {
    return 'in_progress'
  }

  return 'fresh'
}

export function clinicalAccountConsultationButtonLabel(
  mode: ClinicalAccountConsultationCardMode,
): string {
  switch (mode) {
    case 'in_progress':
      return 'Continue booking consultation'
    case 'scheduled':
      return 'View details'
    default:
      return 'Book clinical consultation'
  }
}

export function clinicalAccountConsultationBodyCopy(
  mode: ClinicalAccountConsultationCardMode,
): string {
  switch (mode) {
    case 'in_progress':
      return 'You have a clinical consultation booking in progress. Continue where you left off to finish your visit details.'
    case 'scheduled':
      return 'Your clinical consultation is scheduled. View details below.'
    default:
      return 'Book a clinical visit with a nurse practitioner. Complete insurance details and payment in this window after confirming your slot.'
  }
}

export type ClinicalAccountScheduledSummary = {
  dateLine: string
  timeAndDurationLine: string
}

/** Date/time/duration lines for the account card when a visit is scheduled. */
export function formatClinicalAccountScheduledSummary(
  consultation: Consultation,
): ClinicalAccountScheduledSummary | null {
  const selection = slotSelectionFromConsultation(consultation)
  if (!selection) {
    return null
  }

  const zone = getTimezoneForStateName(consultation.bridgeEligibilityState)

  return {
    dateLine: formatClinicalBookingDate(selection.date, zone),
    timeAndDurationLine: `${formatClinicalSlotRange(selection.slot, zone)} · ${selection.slot.duration} minute consultation`,
  }
}

export type BuildReservedConsultationPatchArgs = {
  consultationId: ConsultationId
  customerId: CustomerId
  consultType: ConsultationConsultType
  existing: Consultation | null
  reservedSlot: AvailabilitySlot
  tellescopeUserId: string
  checkoutExpiresAt: Date
  reservedSlotKey: string
}

/** Optimistic consultation fields after a successful reserveClinicalSlot call. */
export function buildReservedConsultationPatch(
  args: BuildReservedConsultationPatchArgs,
): Consultation {
  const reservedAt = new Date()
  const { existing, reservedSlot, tellescopeUserId, checkoutExpiresAt } = args
  return {
    id: args.consultationId,
    customerId: args.customerId,
    featureKey: 'clinical_consultation',
    consultType: args.consultType,
    lifecycleStatus: 'reserved',
    operationalStatus: existing?.operationalStatus ?? 'draft',
    startTimeInMS: reservedSlot.startTime.getTime(),
    durationMinutes: reservedSlot.duration,
    scheduledStart: reservedSlot.startTime,
    scheduledEnd: reservedSlot.endTime,
    tellescopeUserId,
    checkoutExpiresAt,
    reservedSlotKey: args.reservedSlotKey,
    reservedAt,
    createdAt: existing?.createdAt ?? reservedAt,
    updatedAt: reservedAt,
    ...(existing?.consultantId ? { consultantId: existing.consultantId } : {}),
  }
}

/** Hold expiry for checkout-hold countdown; null when not in a held state. */
export function checkoutExpiresAtFromConsultation(
  consultation: Consultation | null | undefined,
): Date | null {
  const status = consultation?.lifecycleStatus
  if ((status !== 'reserved' && status !== 'payment_pending') || !consultation?.checkoutExpiresAt) {
    return null
  }
  const expiresAt = firestoreTimestampToDate(consultation.checkoutExpiresAt)
  if (!(expiresAt instanceof Date) || Number.isNaN(expiresAt.getTime())) {
    return null
  }
  return expiresAt
}

export function isSettlementPaymentComplete(
  consultation: Consultation | null | undefined,
): boolean {
  const status = consultation?.lifecycleStatus
  return status === 'bridge_settling' || status === 'completed'
}

/** True when backend lifecycle has moved past payment. */
export function isPaymentComplete(consultation: Consultation | null | undefined): boolean {
  const status = consultation?.lifecycleStatus
  return status === 'provisioning' || status === 'scheduled'
}

/** Stripe session create rejects when embedded checkout was already paid. */
export function isClinicalCheckoutAlreadyPaidError(message: string): boolean {
  return /checkout already paid/i.test(message)
}

const CLINICAL_CHECKOUT_SUBMITTED_STORAGE_PREFIX = 'clinical_checkout_submitted:'
const CLINICAL_CHECKOUT_SUBMITTED_TTL_MS = 30 * 60 * 1000

export function markClinicalCheckoutSubmitted(consultationId: string): void {
  try {
    sessionStorage.setItem(
      `${CLINICAL_CHECKOUT_SUBMITTED_STORAGE_PREFIX}${consultationId}`,
      String(Date.now()),
    )
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

export function isClinicalCheckoutRecentlySubmitted(consultationId: string): boolean {
  try {
    const raw = sessionStorage.getItem(
      `${CLINICAL_CHECKOUT_SUBMITTED_STORAGE_PREFIX}${consultationId}`,
    )
    if (!raw) {
      return false
    }
    const submittedAtMs = Number(raw)
    return (
      Number.isFinite(submittedAtMs) &&
      Date.now() - submittedAtMs < CLINICAL_CHECKOUT_SUBMITTED_TTL_MS
    )
  } catch {
    return false
  }
}

export function clearClinicalCheckoutSubmitted(consultationId: string): void {
  try {
    sessionStorage.removeItem(`${CLINICAL_CHECKOUT_SUBMITTED_STORAGE_PREFIX}${consultationId}`)
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

const CLINICAL_INSURANCE_DISPLAY_STORAGE_PREFIX = 'clinical_insurance_display:'

export type ClinicalInsuranceDisplaySnapshot = {
  insuranceCategory: ClinicalInsuranceCategory | null
  insuredPayerDisplayName: string | null
}

export function persistClinicalInsuranceDisplay(
  customerId: string,
  snapshot: ClinicalInsuranceDisplaySnapshot,
): void {
  try {
    sessionStorage.setItem(
      `${CLINICAL_INSURANCE_DISPLAY_STORAGE_PREFIX}${customerId}`,
      JSON.stringify(snapshot),
    )
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

export function restoreClinicalInsuranceDisplay(
  customerId: string,
): ClinicalInsuranceDisplaySnapshot | null {
  try {
    const raw = sessionStorage.getItem(`${CLINICAL_INSURANCE_DISPLAY_STORAGE_PREFIX}${customerId}`)
    if (!raw) {
      return null
    }
    return JSON.parse(raw) as ClinicalInsuranceDisplaySnapshot
  } catch {
    return null
  }
}

const RESERVABLE_LIFECYCLE_STATUSES = new Set<Consultation['lifecycleStatus']>([
  'draft',
  'reserved',
])

/** Early booking-pipeline statuses only; post-scheduled end-of-life statuses are not continuable. */
export function isClinicalConsultationContinuable(
  status: Consultation['lifecycleStatus'],
): boolean {
  return (
    status === 'draft' ||
    status === 'reserved' ||
    status === 'ensuring_bridge_service' ||
    status === 'payment_pending' ||
    status === 'provisioning' ||
    status === 'scheduled' ||
    status === 'pending_settlement_payment'
  )
}

/**
 * Consultation id to pass to reserveClinicalSlot.
 * Reserve never births — caller must create first. Omits terminal/cancel rows
 * (including pending_cancellation) so a stale row cannot be re-reserved.
 */
export function resolveConsultationIdForReserve(args: {
  preferredConsultationId: string | null
  activeConsultationId: string | null
  consultation: Consultation | null | undefined
}): ConsultationId | undefined {
  const id = args.preferredConsultationId ?? args.activeConsultationId
  if (!id) {
    return undefined
  }
  const c = args.consultation
  if (!c || c.id !== id) {
    return undefined
  }
  if (RESERVABLE_LIFECYCLE_STATUSES.has(c.lifecycleStatus)) {
    return id as ConsultationId
  }
  return undefined
}

const CUSTOMER_ABORTABLE_PRE_PROVISIONING_STATUSES = new Set<Consultation['lifecycleStatus']>([
  'draft',
  'reserved',
  'ensuring_bridge_service',
  'payment_pending',
])

/** Intentional close / session swap — customer_aborted for pre-provisioning in-flight statuses. */
export function shouldAttemptClinicalRelease(args: {
  status: Consultation['lifecycleStatus'] | undefined
  customerCheckoutSubmitted: boolean
}): boolean {
  if (args.customerCheckoutSubmitted) {
    return false
  }
  if (!args.status) {
    return false
  }
  return CUSTOMER_ABORTABLE_PRE_PROVISIONING_STATUSES.has(args.status)
}

export function isClinicalCreateBlockedError(error: unknown): boolean {
  if (!(error instanceof FirebaseError)) {
    return false
  }
  if (error.code !== 'functions/failed-precondition') {
    return false
  }
  return /already has an in-progress clinical consultation/i.test(error.message)
}

/** `reserveClinicalSlot` rejected the pick: an active hold or a booked consult overlaps it. */
export function isClinicalSlotTakenError(error: unknown): boolean {
  return error instanceof FirebaseError && error.code === 'functions/already-exists'
}

/** Reserve rejected because lifecycle moved on (often another tab). */
export function isClinicalReserveContinuedInAnotherWindowError(error: unknown): boolean {
  if (!(error instanceof FirebaseError)) {
    return false
  }
  if (error.code !== 'functions/failed-precondition') {
    return false
  }
  return (
    /already reserved/i.test(error.message) || /cannot be reserved from status/i.test(error.message)
  )
}

export function isClinicalBookingInvalidatedError(error: unknown): boolean {
  if (!(error instanceof FirebaseError)) {
    return false
  }
  if (error.code !== 'functions/failed-precondition') {
    return false
  }
  const details = (error as FirebaseError & { details?: { code?: string } }).details
  return details?.code === CLINICAL_BOOKING_INVALIDATED_CODE
}

export function clinicalBookingInvalidatedUserMessage(error: unknown): string {
  if (!isClinicalBookingInvalidatedError(error)) {
    return 'This booking is no longer valid. Please start again.'
  }
  const details = (error as FirebaseError & { details?: { reason?: string } }).details
  switch (details?.reason) {
    case 'hold_expired':
      return 'Your reservation expired. Please start a new booking.'
    case 'demographics_mismatch':
      return 'Your profile changed since you started booking. Please start again.'
    default:
      return 'This booking is no longer valid. Please start again.'
  }
}

/** Optimistic merge after confirmClinicalBooking succeeds. */
export function buildConfirmBookingPatch(
  consultation: Consultation | null | undefined,
  lifecycleStatus: Consultation['lifecycleStatus'],
): Partial<Consultation> {
  if (!consultation) {
    return { lifecycleStatus }
  }
  return { ...consultation, lifecycleStatus }
}
